/**
 * Uzuri Prescription Consultation Form
 * Handles form rendering, validation, submission, cart creation, and customer authentication
 */

(function() {
  'use strict';

  const FIREBASE_FUNCTIONS_URL = window.UZURI_CONFIG?.firebaseFunctionsUrl || '';
  const SHOPIFY_STORE = 'uzuriaesthetics.com';
  const STOREFRONT_TOKEN = '08c729a861d6f7f8eb4f217ab6ca9899';

  const ConsultationForm = {
    drawer: null,
    form: null,
    formSchema: null,
    collectionProductsMap: {},
    initialProduct: null,
    selectedProducts: {},
    currentStep: 0,
    responses: {},
    customer: {},
    photoFiles: {},

    // "Have you filled in our form before?" — see buildSteps().
    returning: null,
    carryOver: null,

    // Auth state
    firebaseApp: null,
    firebaseAuth: null,
    currentUser: null,
    customerProfile: null,
    authView: 'signin',
    authError: null,
    authLoading: false,
    pendingSignupProfile: null,
    resetEmailSent: false,

    init() {
      this.drawer = document.getElementById('prescription-consultation-drawer');
      if (!this.drawer) return;

      this.form = this.drawer.querySelector('.consultation-form');
      this.initFirebaseAuth();
      this.bindEvents();
    },

    initFirebaseAuth() {
      const config = window.UZURI_CONFIG?.firebaseConfig;
      if (!config || !config.apiKey || typeof firebase === 'undefined') {
        return;
      }

      if (!firebase.apps.length) {
        this.firebaseApp = firebase.initializeApp(config);
      } else {
        this.firebaseApp = firebase.apps[0];
      }

      this.firebaseAuth = firebase.auth();

      this.firebaseAuth.onAuthStateChanged((user) => {
        this.currentUser = user;
        if (user) {
          this.loadCustomerProfile();
        } else {
          this.customerProfile = null;
        }
      });
    },

    // ========================
    // Auth Methods
    // ========================

    async signUp(email, password) {
      this.authLoading = true;
      this.authError = null;
      try {
        const credential = await this.firebaseAuth.createUserWithEmailAndPassword(email, password);
        this.currentUser = credential.user;
        return credential.user;
      } catch (error) {
        this.authError = this.getAuthErrorMessage(error.code);
        throw error;
      } finally {
        this.authLoading = false;
      }
    },

    async signIn(email, password) {
      this.authLoading = true;
      this.authError = null;
      try {
        const credential = await this.firebaseAuth.signInWithEmailAndPassword(email, password);
        this.currentUser = credential.user;
        return credential.user;
      } catch (error) {
        this.authError = this.getAuthErrorMessage(error.code);
        throw error;
      } finally {
        this.authLoading = false;
      }
    },

    async sendPasswordReset(email) {
      this.authLoading = true;
      this.authError = null;
      try {
        await this.firebaseAuth.sendPasswordResetEmail(email);
      } catch (error) {
        this.authError = this.getAuthErrorMessage(error.code);
        throw error;
      } finally {
        this.authLoading = false;
      }
    },

    async signOut() {
      await this.firebaseAuth.signOut();
      this.currentUser = null;
      this.customerProfile = null;
    },

    getAuthErrorMessage(code) {
      const messages = {
        'auth/email-already-in-use': 'An account with this email already exists. Please sign in instead.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled. Please contact support.',
        'auth/user-not-found': 'No account found with this email. Please sign up.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/invalid-credential': 'Incorrect email or password. Please try again.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/weak-password': 'Password must be at least 6 characters.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
      };
      return messages[code] || 'An error occurred. Please try again.';
    },

    // ========================
    // Customer Profile Methods
    // ========================

    async loadCustomerProfile() {
      if (!this.currentUser) return;
      try {
        const token = await this.currentUser.getIdToken();
        const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/customerProfile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          this.customerProfile = data.profile;
        }
        // 404 is fine — profile not yet created
      } catch (error) {
        console.error('Error loading customer profile:', error);
      }
    },

    async createCustomerProfile(profileData) {
      if (!this.currentUser) return;
      const token = await this.currentUser.getIdToken();
      const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/customerProfile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...profileData, shopDomain: window.Shopify && window.Shopify.shop ? window.Shopify.shop : window.location.hostname }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create profile');
      }
      const data = await response.json();
      this.customerProfile = data.profile;
    },

    prefillCustomerFromProfile() {
      if (this.customerProfile) {
        this.customer = {
          firstName: this.customerProfile.firstName || '',
          lastName: this.customerProfile.lastName || '',
          email: this.customerProfile.email || this.currentUser?.email || '',
          phone: this.customerProfile.phone || '',
          dateOfBirth: this.customerProfile.dateOfBirth || '',
          address: this.customerProfile.address || '',
          city: this.customerProfile.city || '',
          postcode: this.customerProfile.postcode || '',
        };
      } else if (this.currentUser) {
        this.customer.email = this.currentUser.email || '';
      }
    },

    // ========================
    // Auth UI Rendering
    // ========================

    renderAuthScreen() {
      this.resetEmailSent = false;
      switch (this.authView) {
        case 'signin':
          this.renderSignIn();
          break;
        case 'signup':
          this.renderSignUp();
          break;
        case 'forgot-password':
          this.renderForgotPassword();
          break;
      }
    },

    renderSignIn() {
      this.form.innerHTML = `
        <div class="consultation-header">
          <h2>Sign In</h2>
          <p class="section-description">Sign in to your account to continue with your consultation.</p>
        </div>
        <div class="consultation-body">
          <div class="auth-form-container">
            ${this.authError ? `<div class="auth-error">${this.authError}</div>` : ''}
            <form id="auth-signin-form" class="auth-form" autocomplete="on">
              <div class="form-field">
                <label for="auth-email">Email</label>
                <input
                  type="email"
                  id="auth-email"
                  name="email"
                  autocomplete="username"
                  required
                  placeholder="your@email.com"
                />
              </div>
              <div class="form-field">
                <label for="auth-password">Password</label>
                <input
                  type="password"
                  id="auth-password"
                  name="password"
                  autocomplete="current-password"
                  required
                  minlength="6"
                  placeholder="Enter your password"
                />
              </div>
              <button type="submit" class="btn btn--primary auth-submit-btn" ${this.authLoading ? 'disabled' : ''}>
                ${this.authLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <div class="auth-links">
              <button type="button" class="auth-link" data-auth-view="forgot-password">Forgot your password?</button>
              <p class="auth-switch">Don't have an account? <button type="button" class="auth-link" data-auth-view="signup">Create one</button></p>
            </div>
          </div>
        </div>
      `;
      this.bindAuthEvents();
    },

    renderSignUp() {
      this.form.innerHTML = `
        <div class="consultation-header">
          <h2>Create Account</h2>
          <p class="section-description">Create an account to access prescription consultations.</p>
        </div>
        <div class="consultation-body">
          <div class="auth-form-container">
            ${this.authError ? `<div class="auth-error">${this.authError}</div>` : ''}
            <form id="auth-signup-form" class="auth-form" autocomplete="on">
              <div class="form-fields-row">
                <div class="form-field">
                  <label for="signup-firstName">First Name<span class="required">*</span></label>
                  <input
                    type="text"
                    id="signup-firstName"
                    name="given-name"
                    autocomplete="given-name"
                    required
                    placeholder="First name"
                  />
                </div>
                <div class="form-field">
                  <label for="signup-lastName">Last Name<span class="required">*</span></label>
                  <input
                    type="text"
                    id="signup-lastName"
                    name="family-name"
                    autocomplete="family-name"
                    required
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div class="form-field">
                <label for="signup-email">Email<span class="required">*</span></label>
                <input
                  type="email"
                  id="signup-email"
                  name="email"
                  autocomplete="username"
                  required
                  placeholder="your@email.com"
                />
              </div>
              <div class="form-field">
                <label for="signup-phone">Phone<span class="required">*</span></label>
                <input
                  type="tel"
                  id="signup-phone"
                  name="tel"
                  autocomplete="tel"
                  required
                  placeholder="Phone number"
                />
              </div>
              <div class="form-field">
                <label for="signup-dob">Date of Birth<span class="required">*</span></label>
                <input
                  type="date"
                  id="signup-dob"
                  name="bday"
                  autocomplete="bday"
                  required
                />
              </div>
              <div class="form-field">
                <label for="signup-password">Password<span class="required">*</span></label>
                <input
                  type="password"
                  id="signup-password"
                  name="new-password"
                  autocomplete="new-password"
                  required
                  minlength="6"
                  placeholder="At least 6 characters"
                />
              </div>
              <button type="submit" class="btn btn--primary auth-submit-btn" ${this.authLoading ? 'disabled' : ''}>
                ${this.authLoading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
            <div class="auth-links">
              <p class="auth-switch">Already have an account? <button type="button" class="auth-link" data-auth-view="signin">Sign in</button></p>
            </div>
          </div>
        </div>
      `;
      this.bindAuthEvents();
    },

    renderForgotPassword() {
      this.form.innerHTML = `
        <div class="consultation-header">
          <h2>Reset Password</h2>
          <p class="section-description">Enter your email and we'll send you a link to reset your password.</p>
        </div>
        <div class="consultation-body">
          <div class="auth-form-container">
            ${this.authError ? `<div class="auth-error">${this.authError}</div>` : ''}
            ${this.resetEmailSent ? `<div class="auth-success">Password reset email sent! Check your inbox.</div>` : ''}
            <form id="auth-forgot-form" class="auth-form" autocomplete="on">
              <div class="form-field">
                <label for="forgot-email">Email</label>
                <input
                  type="email"
                  id="forgot-email"
                  name="email"
                  autocomplete="username"
                  required
                  placeholder="your@email.com"
                />
              </div>
              <button type="submit" class="btn btn--primary auth-submit-btn" ${this.authLoading ? 'disabled' : ''}>
                ${this.authLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <div class="auth-links">
              <button type="button" class="auth-link" data-auth-view="signin">Back to sign in</button>
            </div>
          </div>
        </div>
      `;
      this.bindAuthEvents();
    },

    bindAuthEvents() {
      // View switching
      this.form.querySelectorAll('[data-auth-view]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.authError = null;
          this.authView = btn.dataset.authView;
          this.renderAuthScreen();
        });
      });

      // Sign-in form
      const signinForm = this.form.querySelector('#auth-signin-form');
      if (signinForm) {
        signinForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          const email = signinForm.querySelector('#auth-email').value;
          const password = signinForm.querySelector('#auth-password').value;
          try {
            await this.signIn(email, password);
            await this.onAuthSuccess();
          } catch (error) {
            this.renderSignIn();
          }
        });
      }

      // Sign-up form
      const signupForm = this.form.querySelector('#auth-signup-form');
      if (signupForm) {
        signupForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          const email = signupForm.querySelector('#signup-email').value;
          const password = signupForm.querySelector('#signup-password').value;
          const firstName = signupForm.querySelector('#signup-firstName').value;
          const lastName = signupForm.querySelector('#signup-lastName').value;
          const phone = signupForm.querySelector('#signup-phone').value;
          const dob = signupForm.querySelector('#signup-dob').value;

          this.pendingSignupProfile = {
            firstName,
            lastName,
            phone,
            dateOfBirth: dob,
          };

          try {
            await this.signUp(email, password);
            await this.currentUser.updateProfile({
              displayName: `${firstName} ${lastName}`,
            });
            await this.onAuthSuccess();
          } catch (error) {
            this.pendingSignupProfile = null;
            this.renderSignUp();
          }
        });
      }

      // Forgot password form
      const forgotForm = this.form.querySelector('#auth-forgot-form');
      if (forgotForm) {
        forgotForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          const email = forgotForm.querySelector('#forgot-email').value;
          try {
            await this.sendPasswordReset(email);
            this.resetEmailSent = true;
            this.renderForgotPassword();
          } catch (error) {
            this.renderForgotPassword();
          }
        });
      }
    },

    async onAuthSuccess() {
      this.showLoading();

      await this.loadCustomerProfile();

      // If user just signed up and has no profile, create one
      if (!this.customerProfile && this.pendingSignupProfile) {
        try {
          await this.createCustomerProfile(this.pendingSignupProfile);
        } catch (error) {
          console.error('Failed to create customer profile:', error);
        }
        this.pendingSignupProfile = null;
      }

      this.prefillCustomerFromProfile();

      // Continue loading the form
      try {
        this.ensureCollectionPickerFields();
        await this.loadCollectionPickerProducts();

        const allProducts = Object.values(this.collectionProductsMap).flat();
        const matchingProduct = allProducts.find(p => p.productId === this.initialProduct.productId);
        if (matchingProduct) {
          this.selectedProducts[this.initialProduct.variantId].isPrescription = matchingProduct.isPrescription;
        }
        this.ensureInitialProductInPicker();

        this.renderForm();
      } catch (error) {
        this.showError(error.message || 'Failed to load consultation form. Please try again.');
        console.error('Error loading form after auth:', error);
      }
    },

    // ========================
    // Core Event Binding
    // ========================

    bindEvents() {
      // Open drawer buttons
      document.querySelectorAll('[data-consultation-trigger]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const productId = btn.dataset.productId;
          const variantId = btn.dataset.variantId;
          const productTitle = btn.dataset.productTitle;
          const variantTitle = btn.dataset.variantTitle;
          const price = parseInt(btn.dataset.price, 10);
          const collectionHandle = btn.dataset.collectionHandle;

          this.openDrawer({
            productId,
            variantId,
            productTitle,
            variantTitle,
            price,
            collectionHandle
          });
        });
      });

      // Close drawer
      this.drawer.querySelector('[data-close-drawer]')?.addEventListener('click', () => {
        this.closeDrawer();
      });

      // Close on overlay click
      this.drawer.querySelector('.drawer-overlay')?.addEventListener('click', () => {
        this.closeDrawer();
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.closeDrawer();
        }
      });
    },

    async openDrawer(product) {
      this.initialProduct = product;
      this.selectedProducts = {};
      this.selectedProducts[product.variantId] = {
        ...product,
        quantity: 1,
        isPrescription: true
      };
      this.collectionProductsMap = {};
      this.currentStep = 0;
      this.responses = {};
      this.customer = {};
      this.photoFiles = {};
      this.returning = { isReturning: null, changed: null, lookup: null };
      this.carryOver = null;
      this.authView = 'signin';
      this.authError = null;

      this.drawer.classList.add('is-open');
      document.body.classList.add('overflow-hidden');

      this.showLoading();

      try {
        await this.loadFormSchema(product.collectionHandle);

        // Check if form requires auth and user is not signed in
        if (this.formSchema.settings?.requiresAuth && !this.currentUser) {
          this.renderAuthScreen();
          return;
        }

        // Pre-populate customer info if authenticated
        this.prefillCustomerFromProfile();

        this.ensureCollectionPickerFields();
        await this.loadCollectionPickerProducts();

        // Update initial product's isPrescription from loaded collection data
        const allProducts = Object.values(this.collectionProductsMap).flat();
        const matchingProduct = allProducts.find(p => p.productId === this.initialProduct.productId);
        if (matchingProduct) {
          this.selectedProducts[this.initialProduct.variantId].isPrescription = matchingProduct.isPrescription;
        }
        this.ensureInitialProductInPicker();

        this.renderForm();
      } catch (error) {
        this.showError(error.message || 'Failed to load consultation form. Please try again.');
        console.error('Error loading form:', error);
      }
    },

    closeDrawer() {
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      this.resetForm();
    },

    async loadFormSchema(collectionHandle) {
      if (!FIREBASE_FUNCTIONS_URL) {
        throw new Error('Firebase Functions URL is not configured');
      }
      if (!collectionHandle) {
        throw new Error('No collection handle provided');
      }
      const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/getFormByCollection?handle=${collectionHandle}&shopDomain=${SHOPIFY_STORE}`);
      if (!response.ok) {
        throw new Error('Failed to load form');
      }
      const data = await response.json();
      if (!data.form) {
        throw new Error('No consultation form found for this collection');
      }
      this.formSchema = data.form;
      this.branding = data.branding || {};
      this.applyBranding();
    },

    applyBranding() {
      if (!this.branding) return;

      const root = document.documentElement;
      const color = this.branding.primaryColor || '#422566';
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);

      // Compute hover color (lighten by mixing with white)
      const lighten = (v) => Math.min(255, Math.round(v + (255 - v) * 0.2));
      const hoverHex = '#' + [lighten(r), lighten(g), lighten(b)].map(v => v.toString(16).padStart(2, '0')).join('');

      root.style.setProperty('--uzuri-primary', color);
      root.style.setProperty('--uzuri-primary-hover', hoverHex);
      root.style.setProperty('--uzuri-primary-ring', `rgba(${r}, ${g}, ${b}, 0.1)`);
      root.style.setProperty('--uzuri-primary-light', `rgba(${r}, ${g}, ${b}, 0.06)`);

      // Load Google Font if specified
      if (this.branding.fontFamily) {
        const fontName = this.branding.fontFamily;
        const linkId = 'uzuri-google-font';
        if (!document.getElementById(linkId)) {
          const link = document.createElement('link');
          link.id = linkId;
          link.rel = 'stylesheet';
          link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600&display=swap`;
          document.head.appendChild(link);
        }
        root.style.setProperty('--uzuri-font-family', `'${fontName}', sans-serif`);
      }
    },

    /**
     * Check for collectionPicker fields.
     * If the form has no collectionPicker, the user only gets the single
     * product they clicked on — no product selection step is shown.
     */
    ensureCollectionPickerFields() {
      // Nothing to do — forms without a collectionPicker field intentionally
      // skip the product selection step and use only the initial product.
    },

    /**
     * Load products for all collectionPicker fields found in the form schema.
     */
    async loadCollectionPickerProducts() {
      const handles = new Set();
      this.formSchema.sections.forEach(section => {
        section.fields.forEach(field => {
          if ((field.type === 'collectionPicker' || field.type === 'productSelector') && field.collectionHandle) {
            handles.add(field.collectionHandle);
          }
        });
      });

      const uniqueHandles = [...handles];
      const results = await Promise.all(
        uniqueHandles.map(handle => this.fetchCollectionProducts(handle))
      );

      uniqueHandles.forEach((handle, index) => {
        this.collectionProductsMap[handle] = results[index];
      });
    },

    /**
     * Guarantee the product the patient actually clicked is in the picker.
     *
     * The picker is built from the brand collection, and step validation only
     * counts a selection that appears in that list. Two cases used to leave a
     * patient stuck on step 1 with their own product missing: a product tagged
     * for a brand but not in that brand's collection, and a product whose
     * chosen variant isn't the collection query's first variant. Splice it in
     * so it is always visible and always countable.
     */
    ensureInitialProductInPicker() {
      const initial = this.initialProduct;
      if (!initial) return;

      const handle = initial.collectionHandle;
      const list = this.collectionProductsMap[handle] || (this.collectionProductsMap[handle] = []);

      if (list.some(p => String(p.variantId) === String(initial.variantId))) return;

      const entry = {
        productId: String(initial.productId),
        variantId: String(initial.variantId),
        productTitle: initial.productTitle,
        variantTitle: initial.variantTitle && initial.variantTitle !== 'Default Title'
          ? initial.variantTitle
          : '',
        price: initial.price,
        handle: initial.handle || '',
        image: initial.image || null,
        imageAlt: initial.productTitle,
        tags: initial.tags || [],
        isPrescription: true
      };

      // Replace the same product's other variant rather than showing it twice.
      const sameProduct = list.findIndex(p => String(p.productId) === String(initial.productId));
      if (sameProduct >= 0) {
        entry.image = entry.image || list[sameProduct].image;
        entry.handle = entry.handle || list[sameProduct].handle;
        list[sameProduct] = entry;
      } else {
        list.unshift(entry);
      }
    },

    /**
     * Fetch products from a specific Shopify collection.
     * Returns an array of product objects.
     */
    async fetchCollectionProducts(collectionHandle) {
      const query = `
        query getCollectionProducts($handle: String!, $after: String) {
          collection(handle: $handle) {
            products(first: 50, after: $after) {
              pageInfo {
                hasNextPage
              }
              edges {
                cursor
                node {
                  id
                  title
                  handle
                  tags
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  featuredImage {
                    url
                    altText
                  }
                  variants(first: 1) {
                    edges {
                      node {
                        id
                        title
                        price {
                          amount
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      try {
        // Page through the whole collection. A single page of 50 silently hid
        // every product past the 50th — they couldn't be picked, and because
        // step validation only counts products in this list, the patient
        // couldn't get past step 1 at all.
        const edges = [];
        let after = null;
        for (let page = 0; page < 20; page++) {
          const response = await fetch(`https://${SHOPIFY_STORE}/api/2024-10/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
            },
            body: JSON.stringify({
              query,
              variables: { handle: collectionHandle, after }
            })
          });

          if (!response.ok) {
            console.error('Failed to fetch collection products for:', collectionHandle);
            break;
          }

          const data = await response.json();
          const pageProducts = data.data?.collection?.products;
          if (!pageProducts) break;

          edges.push(...pageProducts.edges);
          if (!pageProducts.pageInfo?.hasNextPage || pageProducts.edges.length === 0) break;
          after = pageProducts.edges[pageProducts.edges.length - 1].cursor;
        }

        return edges.map(({ node }) => {
          const variant = node.variants.edges[0]?.node;
          const priceInPence = Math.round(parseFloat(variant?.price?.amount || node.priceRange.minVariantPrice.amount) * 100);

          return {
            productId: node.id.replace('gid://shopify/Product/', ''),
            variantId: variant?.id.replace('gid://shopify/ProductVariant/', ''),
            productTitle: node.title,
            variantTitle: variant?.title !== 'Default Title' ? variant?.title : '',
            price: priceInPence,
            handle: node.handle,
            image: node.featuredImage?.url,
            imageAlt: node.featuredImage?.altText || node.title,
            tags: node.tags || [],
            isPrescription: (node.tags || []).some(tag =>
              tag.toLowerCase().includes('prescription')
            ),
          };
        });
      } catch (error) {
        console.error('Error fetching collection products:', error);
        return [];
      }
    },

    showLoading() {
      this.form.innerHTML = `
        <div class="consultation-loading">
          <div class="loading-spinner"></div>
          <p>Loading consultation form...</p>
        </div>
      `;
    },

    showError(message) {
      this.form.innerHTML = `
        <div class="consultation-error">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <p>${message}</p>
          <button type="button" class="btn btn--primary" onclick="ConsultationForm.closeDrawer()">Close</button>
        </div>
      `;
    },

    /**
     * The steps this patient will actually walk through.
     *
     * Everything downstream (progress, validation, saving, Back/Continue) reads
     * this rather than indexing the form's sections directly, because the list
     * is no longer fixed: a returning patient whose medical history hasn't
     * changed skips the questionnaire sections and goes product → details →
     * review. The product-picker section is never skipped — they still have to
     * say what they're ordering.
     */
    buildSteps() {
      const steps = [{ kind: 'returning' }];

      (this.formSchema.sections || []).forEach(section => {
        const isProductStep = (section.fields || []).some(
          f => f.type === 'collectionPicker' || f.type === 'productSelector'
        );
        if (this.carryOver && !isProductStep) return;
        steps.push({ kind: 'section', section });
      });

      steps.push({ kind: 'details' });
      steps.push({ kind: 'review' });
      return steps;
    },

    currentStepDescriptor() {
      const steps = this.buildSteps();
      return steps[Math.min(this.currentStep, steps.length - 1)];
    },

    renderForm() {
      if (!this.formSchema) return;

      const steps = this.buildSteps();
      const totalSteps = steps.length;
      const step = steps[Math.min(this.currentStep, totalSteps - 1)];

      let html = '';

      // Show authenticated user bar
      if (this.currentUser) {
        html += `
          <div class="auth-user-bar">
            <span class="auth-user-info">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              ${this.currentUser.displayName || this.currentUser.email}
            </span>
            <button type="button" class="auth-signout-btn" data-signout>Sign out</button>
          </div>
        `;
      }

      html += `
        <div class="consultation-header">
          <h2>${this.formSchema.name}</h2>
          <div class="consultation-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${((this.currentStep + 1) / totalSteps) * 100}%"></div>
            </div>
            <span class="progress-text">Step ${this.currentStep + 1} of ${totalSteps}</span>
          </div>
        </div>
        <div class="consultation-body">
      `;

      if (step.kind === 'returning') {
        html += this.renderReturningStep();
      } else if (step.kind === 'section') {
        // Form sections (including collectionPicker fields inline)
        html += this.renderSection(step.section);
      } else if (step.kind === 'details') {
        // Customer info step
        html += this.renderCustomerInfo();
      } else {
        // Review step
        html += this.renderReview();
      }

      html += `
        </div>
        <div class="consultation-footer">
          ${this.currentStep > 0 ? '<button type="button" class="btn btn--secondary" data-prev>Back</button>' : ''}
          ${this.currentStep < totalSteps - 1
            ? '<button type="button" class="btn btn--primary" data-next>Continue</button>'
            : '<button type="button" class="btn btn--primary" data-submit>Submit & Proceed to Checkout</button>'
          }
        </div>
      `;

      this.form.innerHTML = html;
      this.bindFormEvents();
    },

    /**
     * Step 1: "Have you filled in our form before?"
     *
     * Yes + a consultation we can find + nothing changed = skip the medical
     * questions. Anything else falls through to the full questionnaire, which
     * is the safe direction to fail in.
     */
    renderReturningStep() {
      const r = this.returning || {};
      const windowText = r.validityDays
        ? `in the last ${r.validityDays} days`
        : 'recently';

      let html = `
        <div class="form-section">
          <h3>Have you completed our consultation form before?</h3>
          <p class="section-description">
            If you've consulted with us ${windowText} and nothing about your health has changed,
            you can go straight to your order.
          </p>
          <div class="form-fields">
            <fieldset class="form-field" data-field-id="returning-answer">
              <legend>Have you filled in our consultation form before?<span class="required">*</span></legend>
              <div class="radio-group">
                <label class="radio-option">
                  <input type="radio" name="returning-answer" value="yes" ${r.isReturning === true ? 'checked' : ''} />
                  <span>Yes</span>
                </label>
                <label class="radio-option">
                  <input type="radio" name="returning-answer" value="no" ${r.isReturning === false ? 'checked' : ''} />
                  <span>No, this is my first time</span>
                </label>
              </div>
            </fieldset>
      `;

      if (r.isReturning === true) {
        html += `
            <div class="returning-lookup">
              <div class="form-field">
                <label for="returning-email">Email address you used<span class="required">*</span></label>
                <input type="email" id="returning-email" autocomplete="email" value="${r.email || ''}" />
              </div>
              <div class="form-field">
                <label for="returning-dob">Date of birth<span class="required">*</span></label>
                <input type="date" id="returning-dob" autocomplete="bday" value="${r.dob || ''}" />
              </div>
              <button type="button" class="btn btn--secondary" data-returning-check ${r.loading ? 'disabled' : ''}>
                ${r.loading ? 'Checking…' : 'Find my consultation'}
              </button>
        `;

        if (r.lookup && r.lookup.found) {
          const on = r.lookup.submittedAt
            ? new Date(r.lookup.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
            : null;
          html += `
              <p class="returning-result returning-result--found">
                Found your consultation${on ? ` from ${on}` : ''}. Welcome back${r.lookup.firstName ? `, ${r.lookup.firstName}` : ''}.
              </p>
              <fieldset class="form-field" data-field-id="returning-changed">
                <legend>Has anything changed in your medical history since then?<span class="required">*</span></legend>
                <div class="radio-group">
                  <label class="radio-option">
                    <input type="radio" name="returning-changed" value="no" ${r.changed === false ? 'checked' : ''} />
                    <span>No — nothing has changed</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" name="returning-changed" value="yes" ${r.changed === true ? 'checked' : ''} />
                    <span>Yes — something has changed</span>
                  </label>
                </div>
              </fieldset>
          `;
          if (r.changed === true) {
            html += `<p class="returning-result">We'll take you through the questions again so our team has your current details.</p>`;
          }
        } else if (r.lookup && !r.lookup.found) {
          html += `
              <p class="returning-result returning-result--none">
                We couldn't find a consultation ${windowText} for that email and date of birth.
                Please complete the short questionnaire — it only takes a couple of minutes.
              </p>
          `;
        }

        html += `
            </div>
        `;
      }

      html += `
          </div>
        </div>
      `;
      return html;
    },

    /** Looks up the patient's most recent in-date consultation. */
    async runReturningLookup() {
      const emailInput = this.form.querySelector('#returning-email');
      const dobInput = this.form.querySelector('#returning-dob');
      const email = (emailInput?.value || '').trim();
      const dob = (dobInput?.value || '').trim();

      this.returning = { ...this.returning, email, dob };

      const emailProblem = email ? this.emailProblem(email) : 'Please enter the email address you used.';
      if (emailProblem) {
        this.showValidationError(emailProblem);
        return;
      }
      if (!dob) {
        this.showValidationError('Please enter your date of birth.');
        return;
      }

      this.returning = { ...this.returning, loading: true };
      this.renderForm();

      try {
        const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/checkReturningPatient`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, dateOfBirth: dob, shopDomain: SHOPIFY_STORE })
        });
        const data = response.ok ? await response.json() : { found: false };

        this.returning = {
          ...this.returning,
          loading: false,
          lookup: data,
          validityDays: data.validityDays || this.returning.validityDays,
          changed: null
        };
        this.carryOver = null;
      } catch (error) {
        console.error('Returning-patient lookup failed:', error);
        this.returning = { ...this.returning, loading: false, lookup: { found: false } };
        this.carryOver = null;
      }

      this.renderForm();
    },

    renderSection(section) {
      let html = `
        <div class="form-section">
          <h3>${section.title}</h3>
          ${section.description ? `<p class="section-description">${section.description}</p>` : ''}
          <div class="form-fields">
      `;

      section.fields.forEach(field => {
        html += this.renderField(field);
      });

      html += '</div></div>';
      return html;
    },

    renderField(field) {
      // Handle collectionPicker / productSelector fields separately. They
      // both render the same way: a list of products from a Shopify collection.
      if (field.type === 'collectionPicker' || field.type === 'productSelector') {
        return this.renderCollectionPicker(field);
      }

      // Handle photo upload fields
      if (field.type === 'photo') {
        return this.renderPhotoField(field);
      }

      const value = this.responses[field.label] || '';
      const required = field.required ? 'required' : '';
      const requiredMark = field.required ? '<span class="required">*</span>' : '';

      let html = `<div class="form-field" data-field-id="${field.id}">`;

      switch (field.type) {
        case 'text':
        case 'email':
        case 'tel':
        case 'phone':
        case 'date':
        case 'number':
          html += `
            <label for="field-${field.id}">${field.label}${requiredMark}</label>
            <input
              type="${field.type === 'phone' ? 'tel' : field.type}"
              id="field-${field.id}"
              name="${field.id}"
              value="${value}"
              placeholder="${field.placeholder || ''}"
              ${required}
            />
          `;
          break;

        case 'textarea':
          html += `
            <label for="field-${field.id}">${field.label}${requiredMark}</label>
            <textarea
              id="field-${field.id}"
              name="${field.id}"
              placeholder="${field.placeholder || ''}"
              rows="4"
              ${required}
            >${value}</textarea>
          `;
          break;

        case 'select':
          html += `
            <label for="field-${field.id}">${field.label}${requiredMark}</label>
            <select id="field-${field.id}" name="${field.id}" ${required}>
              <option value="">Select an option</option>
              ${field.options?.map(opt => `
                <option value="${opt.value}" ${value === opt.value ? 'selected' : ''}>${opt.label}</option>
              `).join('')}
            </select>
          `;
          break;

        case 'radio':
          html += `<fieldset><legend>${field.label}${requiredMark}</legend>`;
          field.options?.forEach((opt, i) => {
            html += `
              <label class="radio-option">
                <input
                  type="radio"
                  name="${field.id}"
                  value="${opt.value}"
                  ${value === opt.value ? 'checked' : ''}
                  ${required && i === 0 ? 'required' : ''}
                />
                <span>${opt.label}</span>
              </label>
            `;
          });
          html += '</fieldset>';
          break;

        case 'checkbox':
        case 'checkboxGroup':
          const checkedValues = Array.isArray(value) ? value : [];
          html += `<fieldset><legend>${field.label}${requiredMark}</legend>`;
          field.options?.forEach(opt => {
            html += `
              <label class="checkbox-option">
                <input
                  type="checkbox"
                  name="${field.id}"
                  value="${opt.value}"
                  ${checkedValues.includes(opt.value) ? 'checked' : ''}
                />
                <span>${opt.label}</span>
              </label>
            `;
          });
          html += '</fieldset>';
          break;

        case 'yesno':
          html += `<fieldset><legend>${field.label}${requiredMark}</legend>
            <label class="radio-option">
              <input type="radio" name="${field.id}" value="yes" ${value === 'yes' ? 'checked' : ''} ${required ? 'required' : ''} />
              <span>Yes</span>
            </label>
            <label class="radio-option">
              <input type="radio" name="${field.id}" value="no" ${value === 'no' ? 'checked' : ''} />
              <span>No</span>
            </label>
          </fieldset>`;
          break;
      }

      if (field.helpText) {
        html += `<p class="field-help">${field.helpText}</p>`;
      }

      html += '</div>';
      return html;
    },

    renderCollectionPicker(field) {
      const products = this.collectionProductsMap[field.collectionHandle] || [];
      const formatPrice = (pence) => {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP'
        }).format(pence / 100);
      };

      const requiredMark = field.required ? '<span class="required">*</span>' : '';
      const initialProductId = this.initialProduct.productId;

      // Sort so the triggering product appears first
      const sortedProducts = [...products].sort((a, b) => {
        if (a.productId === initialProductId) return -1;
        if (b.productId === initialProductId) return 1;
        return 0;
      });

      let html = `<div class="form-field collection-picker-field" data-field-id="${field.id}" data-collection-handle="${field.collectionHandle}">`;
      html += `<label>${field.label}${requiredMark}</label>`;
      if (field.helpText) {
        html += `<p class="field-help">${field.helpText}</p>`;
      }
      html += `<div class="product-list">`;

      sortedProducts.forEach(product => {
        const isInitial = product.productId === initialProductId;
        const selectedData = this.selectedProducts[product.variantId];
        const quantity = selectedData ? selectedData.quantity : 0;
        const isSelected = quantity > 0;

        html += `
          <div class="product-card ${isSelected ? 'product-card--selected' : ''} ${isInitial ? 'product-card--primary' : ''}"
               data-variant-id="${product.variantId}">
            ${product.image ? `
              <div class="product-card__image">
                <img src="${product.image}" alt="${product.imageAlt}" loading="lazy" />
              </div>
            ` : ''}
            <div class="product-card__info">
              <h4 class="product-card__title">${product.productTitle}</h4>
              ${product.variantTitle ? `<p class="product-card__variant">${product.variantTitle}</p>` : ''}
              <p class="product-card__price">${formatPrice(product.price)}</p>
              ${product.isPrescription ? '<span class="prescription-badge">Prescription</span>' : ''}
            </div>
            <div class="product-card__quantity">
              <button type="button" class="qty-btn qty-minus" data-action="decrease"
                data-product='${JSON.stringify(product)}'
                ${isInitial && quantity <= 1 ? 'disabled' : ''}
                ${!isInitial && quantity <= 0 ? 'disabled' : ''}
              >-</button>
              <span class="qty-value">${quantity}</span>
              <button type="button" class="qty-btn qty-plus" data-action="increase"
                data-product='${JSON.stringify(product)}'
              >+</button>
            </div>
          </div>
        `;
      });

      html += `</div></div>`;
      return html;
    },

    renderPhotoField(field) {
      const required = field.required ? 'required' : '';
      const requiredMark = field.required ? '<span class="required">*</span>' : '';
      const existingFile = this.photoFiles[field.id];

      let html = `<div class="form-field photo-upload-field" data-field-id="${field.id}">`;
      html += `<label for="photo-${field.id}">${field.label}${requiredMark}</label>`;
      if (field.helpText) {
        html += `<p class="field-help">${field.helpText}</p>`;
      }
      html += `
        <div class="photo-upload-area">
          <input type="file" id="photo-${field.id}" name="${field.id}" accept="image/*" ${required}
            data-photo-field-id="${field.id}" class="photo-file-input" />
          <div class="photo-upload-placeholder" id="photo-placeholder-${field.id}" ${existingFile ? 'style="display:none"' : ''}>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
            <p>Click to upload a photo</p>
            <p class="photo-upload-hint">JPEG, PNG or WebP, max 10MB</p>
          </div>
          <div class="photo-preview" id="photo-preview-${field.id}" ${existingFile ? '' : 'style="display:none"'}>
            ${existingFile ? `<img src="${existingFile.dataUrl}" alt="Preview" />` : ''}
            <button type="button" class="photo-remove-btn" data-remove-photo="${field.id}">Remove</button>
          </div>
        </div>
      `;
      html += '</div>';
      return html;
    },

    renderCustomerInfo() {
      return `
        <div class="form-section">
          <h3>Your Information</h3>
          <p class="section-description">Please provide your details for the consultation.</p>
          <div class="form-fields">
            <div class="form-field">
              <label for="customer-firstName">First Name<span class="required">*</span></label>
              <input type="text" id="customer-firstName" name="firstName" autocomplete="given-name" value="${this.customer.firstName || ''}" required />
            </div>
            <div class="form-field">
              <label for="customer-lastName">Last Name<span class="required">*</span></label>
              <input type="text" id="customer-lastName" name="lastName" autocomplete="family-name" value="${this.customer.lastName || ''}" required />
            </div>
            <div class="form-field">
              <label for="customer-email">Email<span class="required">*</span></label>
              <input type="email" id="customer-email" name="email" autocomplete="email" value="${this.customer.email || ''}" required />
            </div>
            <div class="form-field">
              <label for="customer-phone">Phone<span class="required">*</span></label>
              <input type="tel" id="customer-phone" name="phone" autocomplete="tel" value="${this.customer.phone || ''}" required />
            </div>
            <div class="form-field">
              <label for="customer-dateOfBirth">Date of Birth<span class="required">*</span></label>
              <input type="date" id="customer-dateOfBirth" name="dateOfBirth" autocomplete="bday" value="${this.customer.dateOfBirth || ''}" required />
            </div>
            <div class="form-field">
              <label for="customer-address">Address</label>
              <input type="text" id="customer-address" name="address" autocomplete="street-address" value="${this.customer.address || ''}" />
            </div>
            <div class="form-field">
              <label for="customer-city">City</label>
              <input type="text" id="customer-city" name="city" autocomplete="address-level2" value="${this.customer.city || ''}" />
            </div>
            <div class="form-field">
              <label for="customer-postcode">Postcode</label>
              <input type="text" id="customer-postcode" name="postcode" autocomplete="postal-code" value="${this.customer.postcode || ''}" />
            </div>
            <div class="form-field">
              <label class="checkbox-option consent-checkbox">
                <input type="checkbox" name="consent" required />
                <span>${this.formSchema.settings?.consentText || 'I consent to my information being used for this consultation.'}</span>
              </label>
            </div>
          </div>
        </div>
      `;
    },

    renderReview() {
      const formatPrice = (pence) => {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP'
        }).format(pence / 100);
      };

      const selectedList = Object.values(this.selectedProducts);
      const total = selectedList.reduce((sum, p) => sum + (p.price * p.quantity), 0);

      let productsHtml = '';
      selectedList.forEach(product => {
        productsHtml += `
          <div class="review-item">
            <span class="review-label">${product.productTitle}${product.quantity > 1 ? ' (x' + product.quantity + ')' : ''}</span>
            <span class="review-value">${formatPrice(product.price * product.quantity)}</span>
          </div>
        `;
      });

      let responsesHtml = '';

      // The previous answers are copied server-side, so they aren't in
      // this.responses to list here — say what's happening instead of showing
      // an empty "Consultation responses" block.
      if (this.carryOver) {
        responsesHtml += `
          <div class="review-item review-item--carryover">
            <span class="review-label">Medical history</span>
            <span class="review-value">Carried over from your previous consultation — you confirmed nothing has changed.</span>
          </div>
        `;
      }

      this.formSchema.sections.forEach(section => {
        section.fields.forEach(field => {
          if (field.type === 'collectionPicker' || field.type === 'productSelector') return;

          // Show photo preview thumbnails
          if (field.type === 'photo') {
            const photoData = this.photoFiles[field.id];
            if (photoData) {
              responsesHtml += `
                <div class="review-item review-item--photo">
                  <span class="review-label">${field.label}</span>
                  <img src="${photoData.dataUrl}" alt="${field.label}" class="review-photo-thumb" />
                </div>
              `;
            }
            return;
          }

          const value = this.responses[field.label];
          if (value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0)) {
            const displayValue = Array.isArray(value) ? value.join(', ') : value;
            responsesHtml += `
              <div class="review-item">
                <span class="review-label">${field.label}</span>
                <span class="review-value">${displayValue}</span>
              </div>
            `;
          }
        });
      });

      return `
        <div class="form-section review-section">
          <h3>Review Your Consultation</h3>
          <p class="section-description">Please review your information before submitting.</p>

          <div class="review-card">
            <h4>Selected Products (${selectedList.length})</h4>
            ${productsHtml}
            <div class="review-item review-total">
              <span class="review-label"><strong>Total</strong></span>
              <span class="review-value"><strong>${formatPrice(total)}</strong></span>
            </div>
          </div>

          <div class="review-card">
            <h4>Your Information</h4>
            <div class="review-item">
              <span class="review-label">Name</span>
              <span class="review-value">${this.customer.firstName} ${this.customer.lastName}</span>
            </div>
            <div class="review-item">
              <span class="review-label">Email</span>
              <span class="review-value">${this.customer.email}</span>
            </div>
            <div class="review-item">
              <span class="review-label">Phone</span>
              <span class="review-value">${this.customer.phone}</span>
            </div>
            <div class="review-item">
              <span class="review-label">Date of Birth</span>
              <span class="review-value">${this.customer.dateOfBirth}</span>
            </div>
            ${this.customer.address ? `
            <div class="review-item">
              <span class="review-label">Address</span>
              <span class="review-value">${this.customer.address}${this.customer.city ? ', ' + this.customer.city : ''}${this.customer.postcode ? ' ' + this.customer.postcode : ''}</span>
            </div>
            ` : ''}
          </div>

          ${responsesHtml ? `
            <div class="review-card">
              <h4>Consultation Responses</h4>
              ${responsesHtml}
            </div>
          ` : ''}
        </div>
      `;
    },

    bindFormEvents() {
      // "Have you filled in our form before?" controls
      this.form.querySelectorAll('input[name="returning-answer"]').forEach(input => {
        input.addEventListener('change', () => {
          const isReturning = input.value === 'yes';
          this.returning = {
            ...this.returning,
            isReturning,
            ...(isReturning ? {} : { lookup: null, changed: null })
          };
          this.carryOver = null;
          this.renderForm();
        });
      });

      this.form.querySelector('[data-returning-check]')?.addEventListener('click', () => {
        this.runReturningLookup();
      });

      this.form.querySelectorAll('input[name="returning-changed"]').forEach(input => {
        input.addEventListener('change', () => {
          const changed = input.value === 'yes';
          this.returning = { ...this.returning, changed };
          // Only an unchanged history may be carried forward.
          this.carryOver = (!changed && this.returning.lookup?.found)
            ? {
                consultationId: this.returning.lookup.consultationId,
                email: this.returning.email,
                dob: this.returning.dob
              }
            : null;
          this.renderForm();
        });
      });

      // Next button
      this.form.querySelector('[data-next]')?.addEventListener('click', () => {
        if (this.validateCurrentStep()) {
          this.saveCurrentStep();
          this.currentStep++;
          this.renderForm();
        }
      });

      // Prev button
      this.form.querySelector('[data-prev]')?.addEventListener('click', () => {
        this.saveCurrentStep();
        this.currentStep--;
        this.renderForm();
      });

      // Submit button
      this.form.querySelector('[data-submit]')?.addEventListener('click', () => {
        this.submitConsultation();
      });

      // Sign out button
      this.form.querySelector('[data-signout]')?.addEventListener('click', async () => {
        await this.signOut();
        if (this.formSchema?.settings?.requiresAuth) {
          this.renderAuthScreen();
        } else {
          this.customer = {};
          this.renderForm();
        }
      });

      // Collection picker quantity buttons
      this.form.querySelectorAll('.collection-picker-field .qty-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const action = btn.dataset.action;
          const product = JSON.parse(btn.dataset.product);
          const card = btn.closest('.product-card');
          const qtyDisplay = card.querySelector('.qty-value');
          const minusBtn = card.querySelector('.qty-minus');
          const isInitial = product.productId === this.initialProduct.productId;

          let currentQty = this.selectedProducts[product.variantId]?.quantity || 0;

          if (action === 'increase') {
            currentQty++;
          } else if (action === 'decrease') {
            if (isInitial && currentQty <= 1) return;
            currentQty = Math.max(0, currentQty - 1);
          }

          if (currentQty > 0) {
            this.selectedProducts[product.variantId] = {
              ...product,
              quantity: currentQty
            };
            card.classList.add('product-card--selected');
          } else {
            delete this.selectedProducts[product.variantId];
            card.classList.remove('product-card--selected');
          }

          qtyDisplay.textContent = currentQty;

          // Update minus button disabled state
          if (isInitial) {
            minusBtn.disabled = currentQty <= 1;
          } else {
            minusBtn.disabled = currentQty <= 0;
          }
        });
      });

      // Photo upload fields
      this.form.querySelectorAll('.photo-file-input').forEach(input => {
        input.addEventListener('change', (e) => {
          const file = e.target.files[0];
          const fieldId = input.dataset.photoFieldId;
          if (file && fieldId) {
            const reader = new FileReader();
            reader.onload = (ev) => {
              this.photoFiles[fieldId] = { file, dataUrl: ev.target.result };
              const preview = this.form.querySelector(`#photo-preview-${fieldId}`);
              const placeholder = this.form.querySelector(`#photo-placeholder-${fieldId}`);
              if (preview) {
                preview.innerHTML = `<img src="${ev.target.result}" alt="Preview" /><button type="button" class="photo-remove-btn" data-remove-photo="${fieldId}">Remove</button>`;
                preview.style.display = '';
              }
              if (placeholder) placeholder.style.display = 'none';
              // Re-bind remove button
              const removeBtn = preview?.querySelector('.photo-remove-btn');
              if (removeBtn) {
                removeBtn.addEventListener('click', () => this.removePhoto(fieldId));
              }
            };
            reader.readAsDataURL(file);
          }
        });
      });

      // Photo remove buttons
      this.form.querySelectorAll('.photo-remove-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const fieldId = btn.dataset.removePhoto;
          if (fieldId) this.removePhoto(fieldId);
        });
      });
    },

    removePhoto(fieldId) {
      delete this.photoFiles[fieldId];
      const preview = this.form.querySelector(`#photo-preview-${fieldId}`);
      const placeholder = this.form.querySelector(`#photo-placeholder-${fieldId}`);
      const input = this.form.querySelector(`#photo-${fieldId}`);
      if (preview) {
        preview.innerHTML = '';
        preview.style.display = 'none';
      }
      if (placeholder) placeholder.style.display = '';
      if (input) input.value = '';
    },

    validateCurrentStep() {
      const step = this.currentStepDescriptor();

      if (step.kind === 'returning') {
        const r = this.returning || {};
        if (r.isReturning === undefined || r.isReturning === null) {
          this.showValidationError('Please tell us whether you have filled in our form before.');
          return false;
        }
        if (r.isReturning === true && !r.lookup) {
          this.showValidationError('Please enter your email and date of birth, then choose "Find my consultation".');
          return false;
        }
        if (r.isReturning === true && r.lookup?.found && r.changed === null) {
          this.showValidationError('Please tell us whether anything has changed in your medical history.');
          return false;
        }
        return true;
      }

      // Validate form section steps (including collectionPicker fields)
      if (step.kind === 'section') {
        const section = step.section;

        // Validate required collectionPicker / productSelector fields
        const collectionPickerFields = section.fields.filter(
          f => (f.type === 'collectionPicker' || f.type === 'productSelector') && f.required
        );

        for (const cpField of collectionPickerFields) {
          const products = this.collectionProductsMap[cpField.collectionHandle] || [];
          const hasSelection = products.some(
            p => this.selectedProducts[p.variantId]?.quantity > 0
          );
          if (!hasSelection) {
            this.showValidationError(`Please select at least one product for "${cpField.label}".`);
            return false;
          }
        }

        // Validate required photo fields
        const photoFields = section.fields.filter(f => f.type === 'photo' && f.required);
        for (const pf of photoFields) {
          if (!this.photoFiles[pf.id]) {
            this.showValidationError(`Please upload a photo for "${pf.label}".`);
            return false;
          }
        }
      }

      // Validate required HTML fields
      const requiredFields = this.form.querySelectorAll('[required]');
      let valid = true;

      requiredFields.forEach(field => {
        const fieldContainer = field.closest('.form-field');

        if (field.type === 'checkbox' && !field.checked) {
          valid = false;
          fieldContainer?.classList.add('has-error');
        } else if (field.type === 'radio') {
          const name = field.name;
          const checked = this.form.querySelector(`input[name="${name}"]:checked`);
          if (!checked) {
            valid = false;
            field.closest('fieldset')?.classList.add('has-error');
          }
        } else if (!field.value.trim()) {
          valid = false;
          fieldContainer?.classList.add('has-error');
        } else {
          fieldContainer?.classList.remove('has-error');
        }
      });

      if (!valid) {
        this.showValidationError();
        return false;
      }

      // The email is load-bearing: Shopify validates it when the cart is built,
      // and a rejected address used to leave the patient on a 422 error page
      // instead of a checkout. Catch the typos here, where they can still fix it.
      const emailInput = this.form.querySelector('#customer-email');
      if (emailInput && emailInput.value.trim()) {
        const emailProblem = this.emailProblem(emailInput.value.trim());
        if (emailProblem) {
          emailInput.closest('.form-field')?.classList.add('has-error');
          emailInput.focus();
          this.showValidationError(emailProblem);
          return false;
        }
      }

      return valid;
    },

    /**
     * Returns a message when the address looks wrong, or null when it's fine.
     * Shopify rejects addresses whose domain doesn't resolve, so the common
     * finger-slips on the last letter are worth catching by name — one of them
     * ("gmail.con") cost a real order.
     */
    emailProblem(email) {
      if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(email)) {
        return 'Please enter a valid email address.';
      }

      const domain = email.split('@')[1].toLowerCase();
      const TYPOS = {
        'gmail.con': 'gmail.com', 'gmail.co': 'gmail.com', 'gmail.cm': 'gmail.com',
        'gmail.comm': 'gmail.com', 'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com',
        'gmail.vom': 'gmail.com', 'gmail.xom': 'gmail.com', 'gnail.com': 'gmail.com',
        'hotmail.con': 'hotmail.com', 'hotmail.co': 'hotmail.com', 'hotmial.com': 'hotmail.com',
        'hotmail.cm': 'hotmail.com', 'homail.com': 'hotmail.com',
        'yahoo.con': 'yahoo.com', 'yahoo.co': 'yahoo.com', 'yaho.com': 'yahoo.com',
        'outlook.con': 'outlook.com', 'outlook.co': 'outlook.com', 'outloo.com': 'outlook.com',
        'icloud.con': 'icloud.com', 'icloud.co': 'icloud.com', 'iclould.com': 'icloud.com',
        'live.con': 'live.com', 'live.co': 'live.com',
      };
      if (TYPOS[domain]) {
        return `Did you mean @${TYPOS[domain]}? Please check your email address.`;
      }

      // A last part that isn't a plausible TLD (".con", ".c", ".123") is the
      // shape Shopify refuses outright.
      const tld = domain.split('.').pop();
      if (!/^[a-z]{2,}$/.test(tld) || tld === 'con' || tld === 'cim' || tld === 'vom') {
        return 'Please check your email address — that domain does not look right.';
      }

      return null;
    },

    showValidationError(message = 'Please fill in all required fields.') {
      const existing = this.form.querySelector('.validation-error');
      if (existing) existing.remove();

      const error = document.createElement('div');
      error.className = 'validation-error';
      error.textContent = message;
      this.form.querySelector('.consultation-body').prepend(error);

      setTimeout(() => error.remove(), 3000);
    },

    saveCurrentStep() {
      const step = this.currentStepDescriptor();

      if (step.kind === 'returning') {
        const r = this.returning || {};
        this.responses['Have you filled in our consultation form before?'] = r.isReturning ? 'yes' : 'no';
        if (r.isReturning && r.lookup?.found) {
          this.responses['Has anything changed in your medical history?'] = r.changed ? 'yes' : 'no';
        }
        // Don't make them type these twice — and the carry-over is only
        // honoured server-side when they match the consultation being copied.
        if (r.isReturning && r.email) {
          this.customer = { ...this.customer, email: r.email, dateOfBirth: r.dob || this.customer.dateOfBirth };
        }
        return;
      }

      if (step.kind === 'section') {
        // Save section responses (skip collectionPicker and photo fields — handled via event listeners)
        step.section.fields.forEach(field => {
          if (field.type === 'collectionPicker' || field.type === 'productSelector' || field.type === 'photo') return;

          const input = this.form.querySelector(`[name="${field.id}"]`);
          if (!input) return;

          if (field.type === 'checkbox' || field.type === 'checkboxGroup') {
            const checked = this.form.querySelectorAll(`[name="${field.id}"]:checked`);
            this.responses[field.label] = Array.from(checked).map(c => c.value);
          } else if (field.type === 'radio' || field.type === 'yesno') {
            const checked = this.form.querySelector(`[name="${field.id}"]:checked`);
            this.responses[field.label] = checked?.value || '';
          } else {
            this.responses[field.label] = input.value;
          }
        });
      } else if (step.kind === 'details') {
        // Save customer info
        this.customer = {
          firstName: this.form.querySelector('#customer-firstName')?.value || '',
          lastName: this.form.querySelector('#customer-lastName')?.value || '',
          email: this.form.querySelector('#customer-email')?.value || '',
          phone: this.form.querySelector('#customer-phone')?.value || '',
          dateOfBirth: this.form.querySelector('#customer-dateOfBirth')?.value || '',
          address: this.form.querySelector('#customer-address')?.value || '',
          city: this.form.querySelector('#customer-city')?.value || '',
          postcode: this.form.querySelector('#customer-postcode')?.value || '',
        };
      }
    },

    async submitConsultation() {
      const submitBtn = this.form.querySelector('[data-submit]');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';

      try {
        let firstPhotoUrl = null;

        // Upload all photos from photo fields
        const photoFieldIds = Object.keys(this.photoFiles);
        for (const fieldId of photoFieldIds) {
          const { file } = this.photoFiles[fieldId];
          const base64Data = await this.fileToBase64(file);

          const uploadResponse = await fetch(`${FIREBASE_FUNCTIONS_URL}/uploadPhoto`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              imageData: base64Data,
              fileName: file.name,
              mimeType: file.type
            })
          });

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to upload photo');
          }

          const uploadData = await uploadResponse.json();
          if (!firstPhotoUrl) firstPhotoUrl = uploadData.url;

          // Store URL in responses keyed by field label
          const field = this.formSchema.sections.flatMap(s => s.fields).find(f => f.id === fieldId);
          if (field) {
            this.responses[field.label] = uploadData.url;
          }
        }

        // Submit consultation with all selected products
        const payload = {
          formId: this.formSchema.id,
          customer: this.customer,
          responses: this.responses,
          products: Object.values(this.selectedProducts).map(product => ({
            productId: parseInt(product.productId, 10),
            variantId: parseInt(product.variantId, 10),
            title: product.productTitle,
            variantTitle: product.variantTitle || '',
            price: product.price,
            quantity: product.quantity,
            isPrescription: product.isPrescription
          })),
          photoUrl: firstPhotoUrl,
          customerUid: this.currentUser?.uid || undefined,
          consentGiven: true,
          shopDomain: 'uzuriaesthetics.com',
          // Re-verified server-side against this submission's email and date of
          // birth before any previous answers are copied.
          ...(this.carryOver && { previousConsultationId: this.carryOver.consultationId })
        };

        console.log('Submitting consultation payload:', payload);

        const headers = { 'Content-Type': 'application/json' };
        if (this.currentUser) {
          try {
            const token = await this.currentUser.getIdToken(true);
            headers['Authorization'] = `Bearer ${token}`;
          } catch (err) {
            console.warn('Could not get auth token for submission:', err);
          }
        }

        const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/submitConsultation`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Submission validation error:', error);
          throw new Error(error.error || error.message || 'Failed to submit consultation');
        }

        const result = await response.json();

        // Show success and redirect to cart
        this.showSuccess(result.cartUrl, result.rejectedContactFields || []);

      } catch (error) {
        console.error('Submission error:', error);
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit & Proceed to Checkout';

        const errorDiv = document.createElement('div');
        errorDiv.className = 'submission-error';
        errorDiv.textContent = error.message || 'Failed to submit consultation. Please try again.';
        this.form.querySelector('.consultation-body').prepend(errorDiv);
      }
    },

    showSuccess(cartUrl, rejectedContactFields = []) {
      const tick = `
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>`;
      const heading = this.formSchema.settings?.successMessage
        || 'Your consultation has been submitted successfully.';

      // No cart could be built at all (no Shopify config, or no products). The
      // consultation is saved — say so rather than sending them to "undefined".
      if (!cartUrl) {
        this.form.innerHTML = `
          <div class="consultation-success">
            ${tick}
            <h3>${heading}</h3>
            <p>Our team will review your request and be in touch to complete your order.</p>
          </div>
        `;
        return;
      }

      // Shopify wouldn't accept a contact detail, so it has been left out of the
      // checkout. Don't auto-redirect over the top of that message.
      const emailRejected = rejectedContactFields.includes('email');
      const note = emailRejected
        ? `<p class="consultation-success__warning">We couldn't verify the email address you gave us, so it hasn't been filled in at checkout. Please enter it again on the next page, and let us know if it was wrong so we can reach you about this consultation.</p>`
        : '';

      this.form.innerHTML = `
        <div class="consultation-success">
          ${tick}
          <h3>${heading}</h3>
          <p>Please proceed to checkout to complete your order. If there is any reason we are unable to dispense your selected products, we will contact you directly.</p>
          ${note}
          <a href="${cartUrl}" class="btn btn--primary">Proceed to Checkout</a>
        </div>
      `;

      if (emailRejected) return;

      // Auto redirect after 3 seconds
      setTimeout(() => {
        window.location.href = cartUrl;
      }, 3000);
    },

    resetForm() {
      this.formSchema = null;
      this.collectionProductsMap = {};
      this.initialProduct = null;
      this.selectedProducts = {};
      this.currentStep = 0;
      this.responses = {};
      this.customer = {};
      this.photoFiles = {};
      this.returning = null;
      this.carryOver = null;
      this.authView = 'signin';
      this.authError = null;
      this.pendingSignupProfile = null;
      this.resetEmailSent = false;
      this.form.innerHTML = '';
    },

    // Convert file to base64 string
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Remove data:image/xxx;base64, prefix
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ConsultationForm.init());
  } else {
    ConsultationForm.init();
  }

  // Expose globally for external access
  window.ConsultationForm = ConsultationForm;
})();
